/**
 * Main Application Component
 *
 * This is the root component that handles MSAL (Microsoft Authentication Library)
 * initialization and provides the authentication context to the entire React application.
 *
 * Architecture Overview:
 * - Initializes MSAL instance dynamically from backend configuration
 * - Provides authentication context via MsalProvider
 * - Wraps the app with error boundaries and global contexts
 * - Shows loading screen during MSAL initialization
 *
 * Key Features:
 * - Singleton MSAL instance to prevent duplicate authentication contexts
 * - Dynamic configuration loading from backend API endpoint
 * - Secure authentication flow with proper error handling
 * - Context providers for user state and global canvas state
 *
 * @example
 * ```json
 * // MSAL Configuration Response from Backend
 * {
 *   "clientId": "12345678-1234-1234-1234-123456789012",
 *   "tenantId": "87654321-4321-4321-4321-210987654321",
 *   "authority": "https://login.microsoftonline.com/87654321-4321-4321-4321-210987654321",
 *   "redirectUri": "https://lexai.prosperfinance.com"
 * }
 *
 * // Application State During Loading
 * {
 *   "msalInstance": null,
 *   "isLoading": true,
 *   "error": null
 * }
 *
 * // Application State After Initialization
 * {
 *   "msalInstance": {
 *     "config": {
 *       "auth": {
 *         "clientId": "12345678-1234-1234-1234-123456789012",
 *         "authority": "https://login.microsoftonline.com/87654321-4321-4321-4321-210987654321"
 *       },
 *       "cache": {
 *         "cacheLocation": "sessionStorage"
 *       }
 *     },
 *     "isInitialized": true
 *   },
 *   "isLoading": false,
 *   "error": null
 * }
 * ```
 */

import React, { useState, useEffect } from 'react';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { updateMsalConfig } from './authConfig';
import AuthenticatedApp from './components/AuthenticatedApp';
import ErrorBoundary from './components/ErrorBoundary';
import { UserProvider } from './contexts/UserContext';
import { GlobalCanvasProvider } from './contexts/GlobalCanvasContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './App.css';
import './styles/artifacts.css';
import './styles/canvas-tabs.css';
import './styles/detail-views.css';

// Singleton instance to prevent multiple MSAL initializations
let msalInstanceSingleton: PublicClientApplication | null = null;

/**
 * Root React Component for LexAI Dashboard
 *
 * Handles the complete application lifecycle including MSAL initialization,
 * authentication state management, and context provider setup.
 *
 * @returns React element with layered context providers and loading states
 *
 * @example
 * ```typescript
 * // Component Usage (typically handled by React Router)
 * import App from './App';
 * import { BrowserRouter } from 'react-router-dom';
 *
 * function RootApp() {
 *   return (
 *     <BrowserRouter>
 *       <App />
 *     </BrowserRouter>
 *   );
 * }
 * ```
 */
function App() {
  // State for MSAL instance and loading status
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    /**
     * Initialize MSAL (Microsoft Authentication Library)
     *
     * This function handles the dynamic loading of MSAL configuration from the backend
     * and creates a singleton instance to ensure consistent authentication state.
     *
     * Flow:
     * 1. Check for existing singleton instance (prevents duplicate initialization)
     * 2. Fetch configuration from backend API (/api/auth/config)
     * 3. Create and initialize MSAL PublicClientApplication
     * 4. Store singleton reference for future use
     */
    const initializeMsal = async () => {
      try {
        // Prevent duplicate MSAL instances across re-renders
        if (msalInstanceSingleton) {
          setMsalInstance(msalInstanceSingleton);
          setIsLoading(false);
          return;
        }

        // Dynamically load MSAL configuration from backend to avoid hardcoding secrets
        const config = await updateMsalConfig();

        // Create MSAL instance with configuration loaded from backend
        const instance = new PublicClientApplication(config);
        await instance.initialize();

        // Handle login redirect callbacks (required for redirect flow)
        const redirectResult = await instance.handleRedirectPromise();

        // Security: Clean auth code from URL after MSAL processes the redirect
        if (redirectResult || window.location.hash.includes('code=')) {
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        // Set active account if available (Microsoft best practice)
        const accounts = instance.getAllAccounts();
        if (accounts.length > 0) {
          // Set the first account as active (or implement account selection logic)
          instance.setActiveAccount(accounts[0]);
          console.log('✅ Active account set:', accounts[0].username);
        }

        // Store singleton reference to prevent recreation on component re-mounts
        msalInstanceSingleton = instance;
        setMsalInstance(instance);
      } catch (error) {
        console.error('Failed to initialize MSAL:', error);
        // Error handling: Could render an error component instead of loading screen
      } finally {
        setIsLoading(false);
      }
    };

    initializeMsal();
  }, []);

  // Show loading screen while MSAL initializes
  if (isLoading || !msalInstance) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--dark-primary, #0f0f23)',
        color: 'var(--text-primary, #ffffff)',
        fontFamily: '"Inter", sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          {/* Loading spinner animation */}
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid var(--accent-color, #6366f1)',
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1rem'
          }}></div>
          <p>Loading Lex AI...</p>
        </div>
      </div>
    );
  }

  /**
   * Main Application Render Tree
   *
   * This return statement creates the layered context providers that wrap the entire application:
   *
   * ErrorBoundary: Catches and handles JavaScript errors gracefully
   * MsalProvider: Provides MSAL authentication context to all child components
   * UserProvider: Manages user state and profile information
   * GlobalCanvasProvider: Manages global canvas state for the application
   *
   * This layered approach ensures that authentication and global state are available
   * throughout the component tree without prop drilling.
   */
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <MsalProvider instance={msalInstance}>
          <UserProvider>
            <GlobalCanvasProvider>
              <div className="App">
                <AuthenticatedApp />
              </div>
            </GlobalCanvasProvider>
          </UserProvider>
        </MsalProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
